/**
 * Fusion Canvas (sandbox demo) — /fusion (auth) and /demo/fusion (guest).
 * Three tabs over one case (Intake Builder spec): BUILD (author — card board +
 * guided start), CANVAS (the fused graph — see how it ties together), JOURNAL
 * (recap how you got here). Fully isolated from the crypto Workbench.
 * See docs/FUSION_WORKBENCH.md + research/FUSION_INTAKE_BUILDER_SPEC.md.
 */
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type cytoscape from 'cytoscape';
import { FolderOpen, Plus } from 'lucide-react';
import { FusionToolbar } from '../components/fusion/FusionToolbar';
import { FusionCanvas } from '../components/fusion/FusionCanvas';
import { FusionInspector } from '../components/fusion/FusionInspector';
import { FusionNarration } from '../components/fusion/FusionNarration';
import { FusionTimeline } from '../components/fusion/FusionTimeline';
import { FusionEvidenceLedger } from '../components/fusion/FusionEvidenceLedger';
import { FusionBankImport } from '../components/fusion/FusionBankImport';
import { FusionCaseFiles } from '../components/fusion/FusionCaseFiles';
import { FusionCanvasStage } from '../components/fusion/FusionCanvasStage';
import { FusionDemoPicker } from '../components/fusion/FusionDemoPicker';
import { FusionGraphLegend } from '../components/fusion/FusionGraphLegend';
import { FusionCaseEntryModal, type FusionCaseEntryMode } from '../components/fusion/FusionCaseEntryModal';
import { RadialMenu } from '../components/fusion/RadialMenu';
import { RunRail } from '../components/fusion/RunRail';
import { PlaybookNarration } from '../components/fusion/PlaybookNarration';
import { IntakeBoard, type IntakeBuildPath } from '../components/fusion/intake/IntakeBoard';
import { useIntakeStore } from '../stores/useIntakeStore';
import { useFusionStore } from '../stores/useFusionStore';
import { Journal } from '../components/fusion/intake/Journal';
import { CaseReplay } from '../components/fusion/intake/CaseReplay';
import { DemoGuide } from '../components/DemoGuide';
import {
  fusionDemoAsGuideDemo,
  getFusionDemo,
  type FusionDemo,
  type FusionDemoAction,
} from '../data/fusionDemos';
import { runPlaybook } from '../case-canvas/playbookRunner';
import { isGuestDemo } from '../utils/guestDemo';

type MainTab = 'build' | 'canvas' | 'journal';
type AsideTab = 'inspector' | 'ledger' | 'import' | 'files';

function applyFusionDemoActions(
  actions: FusionDemoAction[],
  setMain: (m: MainTab) => void,
  setTab: (t: AsideTab) => void,
) {
  const store = useFusionStore.getState();
  for (const a of actions) {
    switch (a.type) {
      case 'setMain':
        setMain(a.main);
        break;
      case 'setStep':
        store.setStep(a.step);
        break;
      case 'play':
        store.setPlaying(a.playing ?? true);
        break;
      case 'courtSafe':
        store.setCourtSafe(a.on);
        break;
      case 'showDemo':
        store.setShowDemo(a.on);
        break;
      case 'selectNode':
        store.select({ id: a.id, kind: 'node' });
        {
          const cy = (window as unknown as { __fusionCy?: cytoscape.Core }).__fusionCy;
          const n = cy?.getElementById(a.id);
          if (cy && n && !n.empty()) cy.animate({ center: { eles: n } }, { duration: 280 });
        }
        break;
      case 'openAside':
        setMain('canvas');
        setTab(a.tab);
        break;
      case 'runPlaybook':
        void runPlaybook();
        break;
      default:
        break;
    }
  }
}

export default function FusionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Front door is Build (guided start) — Canvas is the projection once cards run.
  const [main, setMain] = useState<MainTab>('build');
  const [tab, setTab] = useState<AsideTab>('inspector');
  const [dragOver, setDragOver] = useState(false);
  // Day 21: wait for sandbox bind + optional graph hydrate before mounting the
  // canvas so restored subject-hub edges aren't raced by an empty onCy restore.
  const [canvasReady, setCanvasReady] = useState(false);
  const [sandboxCaseId, setSandboxCaseId] = useState<string | undefined>();

  const [activeDemoId, setActiveDemoId] = useState<string | null>(null);
  const [demoGuideVisible, setDemoGuideVisible] = useState(false);
  const [demoPickerOpen, setDemoPickerOpen] = useState(false);
  const [caseEntryOpen, setCaseEntryOpen] = useState(false);
  const [caseEntryMode, setCaseEntryMode] = useState<FusionCaseEntryMode>('new');
  /** Build-tab path: chooser until demos or case is picked (keeps demo seed off). */
  const [buildPath, setBuildPath] = useState<IntakeBuildPath>('chooser');
  const guest = isGuestDemo();

  const activeDemo = getFusionDemo(activeDemoId);
  const showLegend = useFusionStore((s) => s.showLegend);
  const showDemo = useFusionStore((s) => s.showDemo);
  const setShowDemo = useFusionStore((s) => s.setShowDemo);

  const openCaseEntry = (mode: FusionCaseEntryMode) => {
    setCaseEntryMode(mode);
    setCaseEntryOpen(true);
  };

  const onExploreDemos = useCallback(() => {
    setBuildPath('demo');
    setShowDemo(true);
    setMain('build');
    setDemoPickerOpen(true);
  }, [setShowDemo]);

  const onCreateCaseFromBuild = useCallback(() => {
    setShowDemo(false);
    setActiveDemoId(null);
    setDemoGuideVisible(false);
    if (guest) {
      setBuildPath('demo');
      useIntakeStore.getState().reset();
      if (sandboxCaseId) useIntakeStore.getState().setCaseId(sandboxCaseId);
      setMain('build');
      useIntakeStore.getState().startFrom('blank', []);
      return;
    }
    openCaseEntry('new');
  }, [guest, sandboxCaseId, setShowDemo]);


  const loadFusionDemo = useCallback((demo: FusionDemo, opts?: { persistUrl?: boolean }) => {
    setBuildPath('demo');
    setShowDemo(true);
    applyFusionDemoActions(demo.actions, setMain, setTab);
    setActiveDemoId(demo.id);
    setDemoGuideVisible(true);
    setDemoPickerOpen(false);
    if (opts?.persistUrl !== false) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('demo', demo.id);
        return next;
      }, { replace: true });
    }
  }, [setSearchParams, setShowDemo]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) { setTab('import'); window.dispatchEvent(new CustomEvent('fusion:drop-file', { detail: file })); }
  };

  useEffect(() => {
    const onOpenTab = (e: Event) => {
      const which = (e as CustomEvent).detail;
      if (which === 'import' || which === 'ledger' || which === 'inspector' || which === 'files') {
        setMain('canvas');
        setTab(which);
      }
    };
    const toCanvas = () => setMain('canvas');
    const onOpenBuildCard = () => {
      setMain('build');
    };
    window.addEventListener('fusion:open-tab', onOpenTab);
    window.addEventListener('intake:goto-canvas', toCanvas);
    window.addEventListener('fusion:open-build-card', onOpenBuildCard);
    return () => {
      window.removeEventListener('fusion:open-tab', onOpenTab);
      window.removeEventListener('intake:goto-canvas', toCanvas);
      window.removeEventListener('fusion:open-build-card', onOpenBuildCard);
    };
  }, []);

  // History spine: bind this surface to the caller's Fusion Sandbox case so card
  // runs / entities / journal events persist via the existing case_events paths
  // (mirrors CaseCanvasPage's bind-on-mount / unbind-on-unmount). Best-effort —
  // if the endpoint is unreachable the surface stays client-only, as before.
  // Guest /demo/fusion skips the API entirely (synthetic seed only).
  // Day 21: after bind, hydrate subject-hub / derived edges from GET /graph so
  // a reload restores edges that POST /canvas/{id}/edges persisted.
  useEffect(() => {
    if (isGuestDemo()) {
      setCanvasReady(true);
      return;
    }
    const API = ((import.meta.env as unknown as Record<string, string>).VITE_API_URL) || 'http://localhost:8000/api/v1';
    const token = localStorage.getItem('token') || '';
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/canvas/sandbox`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json() as { case_id?: string };
          if (!cancelled && d?.case_id) {
            useIntakeStore.getState().setCaseId(d.case_id);
            setSandboxCaseId(d.case_id);
            // Warm Journal spine early: flush outbox + seed empty sandbox so
            // Increment C Replay is ready before the officer opens the tab.
            void import('../case-canvas/intake/journalSpine').then(async (j) => {
              try {
                await j.flushOutbox(d.case_id!);
                if (useFusionStore.getState().showDemo) {
                  await j.ensureDemoSeedEvents(d.case_id!);
                } else {
                  const entries = await j.fetchCaseEvents(d.case_id!);
                  j.writeEventsCache(d.case_id!, entries);
                }
              } catch { /* Journal still loads on-demand */ }
            });
            try {
              const { fetchCaseGraph, envelopeToElements } = await import('../services/caseCanvas');
              const g = await fetchCaseGraph(d.case_id);
              if (!cancelled && g?.nodes?.length) {
                const els = envelopeToElements(g.nodes || [], g.edges || []);
                const bags = els.map((el) => ({
                  ...(el.data as Record<string, unknown>),
                  id: String((el.data as { id: string }).id),
                }));
                useIntakeStore.getState().saveUserElements(bags);
              }
            } catch { /* projection optional */ }
          }
        }
      } catch { /* stay client-only */ }
      finally {
        if (!cancelled) setCanvasReady(true);
      }
    })();
    return () => { cancelled = true; useIntakeStore.getState().setCaseId(undefined); };
  }, []);

  // Deep-link: /fusion?demo=<id> or /demo/fusion?demo=<id> loads once canvas-ready.
  useEffect(() => {
    if (!canvasReady) return;
    const id = searchParams.get('demo');
    if (!id || id === activeDemoId) return;
    const demo = getFusionDemo(id);
    if (demo) loadFusionDemo(demo, { persistUrl: false });
  }, [canvasReady, searchParams, activeDemoId, loadFusionDemo]);

  // Keep demo seed OFF until the officer picks Explore demos (or deep-links a demo).
  useEffect(() => {
    if (searchParams.get('demo')) return;
    if (buildPath === 'chooser') setShowDemo(false);
  }, [buildPath, searchParams, setShowDemo]);

  const MAIN_TABS: { id: MainTab; label: string; hint: string }[] = [
    { id: 'build', label: 'Build', hint: 'author — drop evidence cards' },
    { id: 'canvas', label: 'Canvas', hint: 'see how it ties together' },
    { id: 'journal', label: 'Journal', hint: 'recap how you got here' },
  ];

  return (
    <div className="absolute inset-0 flex flex-col min-h-0 overflow-hidden bg-background">
      <div
        className="flex items-center gap-2 border-b border-border bg-card/50 text-sm shrink-0 px-2 min-h-[2.75rem]"
        data-tour="fusion-main-tabs"
      >
        <h1 className="pl-2 pr-1 text-sm font-semibold leading-tight truncate shrink-0">
          Fusion Canvas
        </h1>
        <span
          data-tour="fusion-demo-pill"
          title={
            buildPath === 'demo'
              ? 'Training sandbox — demo scenarios stay separate from live LE cases'
              : 'Pick Explore demos on Build, or Create a new case for live work'
          }
          className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
            buildPath === 'demo'
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100'
              : 'border-border bg-secondary/50 text-muted-foreground'
          }`}
        >
          {buildPath === 'demo' ? 'Demo' : 'Sandbox'}
        </span>
        <div className="flex items-stretch shrink-0">
          {MAIN_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setMain(t.id)}
              title={t.hint}
              data-tour={t.id === 'build' ? 'fusion-tab-build' : t.id === 'journal' ? 'fusion-tab-journal' : 'fusion-tab-canvas'}
              className={`px-4 py-2 font-medium transition-colors ${main === t.id ? 'border-b-2 border-primary text-foreground bg-secondary/30' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-0 flex justify-end items-center gap-1.5 overflow-x-auto styled-scrollbar py-1.5 pr-1">
          <button
            type="button"
            onClick={() => openCaseEntry('new')}
            disabled={guest}
            data-tour="fusion-new-case"
            title={guest ? 'Sign in to create a real Fusion case' : 'Create a named LE case and open it on Fusion Canvas'}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-primary/40 bg-primary text-primary-foreground hover:bg-brand-gold-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" /> New case
          </button>
          <button
            type="button"
            onClick={() => openCaseEntry('open')}
            disabled={guest}
            data-tour="fusion-open-case"
            title={guest ? 'Sign in to open a real case' : 'Open an existing LE case on Fusion Canvas'}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FolderOpen className="w-3.5 h-3.5" /> Open case
          </button>
        </div>
      </div>

      {main === 'canvas' && (
        <FusionToolbar
          showDemoGuideButton={!!activeDemo}
          demoGuideOpen={demoGuideVisible}
          onToggleDemoGuide={() => setDemoGuideVisible((v) => !v)}
          onOpenDemoLibrary={() => setDemoPickerOpen(true)}
          onCleared={() => {
            setActiveDemoId(null);
            setDemoGuideVisible(false);
            setMain('build');
          }}
        />
      )}

      {main === 'build' && (
        <div className="flex-1 min-h-0">
          <IntakeBoard
            showPathChooser
            buildPath={buildPath}
            onExploreDemos={onExploreDemos}
            onCreateCase={onCreateCaseFromBuild}
            onResetPath={() => {
              setBuildPath('chooser');
              setShowDemo(false);
              setActiveDemoId(null);
              setDemoGuideVisible(false);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('demo');
                return next;
              }, { replace: true });
            }}
          />
        </div>
      )}
      {main === 'journal' && <div className="flex-1 min-h-0"><Journal /></div>}

      {main === 'canvas' && (
        <>
          <FusionCanvasStage
            forceAsideExpanded={tab === 'ledger' || tab === 'import' || tab === 'files'}
            aside={(
              <>
                <div className="flex border-b border-border text-xs shrink-0 bg-card/50 overflow-x-auto">
                  <button onClick={() => setTab('inspector')} data-tour="fusion-aside-inspector"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'inspector' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Inspector
                  </button>
                  <button onClick={() => setTab('ledger')} data-tour="fusion-aside-ledger"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'ledger' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Court-safe
                  </button>
                  <button onClick={() => setTab('import')} data-tour="fusion-aside-import"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'import' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Bank import
                  </button>
                  <button onClick={() => setTab('files')} data-tour="fusion-aside-files"
                    className={`flex-1 px-2.5 py-2.5 font-medium transition-colors whitespace-nowrap ${tab === 'files' ? 'bg-secondary text-foreground border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    Case files
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0">
                  {tab === 'inspector' && <FusionInspector caseId={sandboxCaseId} />}
                  {tab === 'ledger' && (
                    <FusionEvidenceLedger
                      source={showDemo ? 'seed' : 'canvas'}
                      onClose={() => setTab('inspector')}
                    />
                  )}
                  {tab === 'import' && <FusionBankImport onOpenCaseFiles={() => setTab('files')} />}
                  {tab === 'files' && <FusionCaseFiles />}
                </div>
              </>
            )}
          >
            <div
              className="absolute inset-0"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <FusionNarration />
              {canvasReady
                ? <FusionCanvas key={sandboxCaseId || 'client'} caseId={sandboxCaseId} />
                : <div className="absolute inset-0 grid place-items-center text-xs text-muted-foreground">Loading Fusion Canvas…</div>}
              {showLegend && <FusionGraphLegend />}
              <PlaybookNarration />
              <FusionTimeline />
              <CaseReplay />
              {dragOver && (
                <div className="absolute inset-3 z-30 rounded-lg border-2 border-dashed border-primary bg-primary/10 grid place-items-center pointer-events-none">
                  <div className="text-sm font-semibold text-primary bg-card border border-primary/40 rounded-md px-4 py-2 shadow-overlay">
                    Drop a bank statement to ingest → bank surface
                  </div>
                </div>
              )}
            </div>
          </FusionCanvasStage>
          <RunRail />
          <RadialMenu />
        </>
      )}

      <FusionDemoPicker
        open={demoPickerOpen}
        onClose={() => setDemoPickerOpen(false)}
        onSelect={(demo) => loadFusionDemo(demo)}
      />

      <FusionCaseEntryModal
        open={caseEntryOpen}
        initialMode={caseEntryMode}
        onClose={() => setCaseEntryOpen(false)}
      />

      {demoGuideVisible && activeDemo && (
        <DemoGuide
          demo={fusionDemoAsGuideDemo(activeDemo)}
          surface="fusion"
          fusionUiMap={activeDemo.navHints}
          onClose={() => setDemoGuideVisible(false)}
        />
      )}
    </div>
  );
}
